import type { History, Location, LocationDescriptorObject } from 'history';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { BridgeClientError } from '..';

export interface HistoryApiProvider {
	extensionBasePath?: string;
	history?: History;
}

const isConnectionClosedError = (error: unknown): boolean =>
	typeof error === 'object' &&
	error !== null &&
	(error as { code?: unknown }).code === 'CONNECTION_CLOSED';

export const createExtensionPathParsers = (extensionBasePath: string) => {
	return {
		extensionRouteToFullPath: (extensionRoute: string) =>
			`${extensionBasePath}${extensionRoute ?? ''}`,
		fullPathToExtensionRoute: (fullLocation: string): string => {
			const extensionRoute = fullLocation.split(extensionBasePath).pop();
			return extensionRoute ? extensionRoute : '/';
		},
	};
};

export const createHistory = ({
	history,
	extensionBasePath,
	unlistenRegistry,
}: HistoryApiProvider & {
	/**
	 * Provided by the v2 bridge so it can remove active `listen` subscriptions on teardown.
	 * Absent on the v1 path.
	 */
	unlistenRegistry?: Set<() => void>;
}): History | undefined => {
	if (!history || !extensionBasePath) {
		throw new BridgeClientError('History API is not available within this module.');
	}

	const { extensionRouteToFullPath, fullPathToExtensionRoute } =
		createExtensionPathParsers(extensionBasePath);

	const parseLocation = (
		location: Location<any>,
		parser: typeof extensionRouteToFullPath | typeof fullPathToExtensionRoute,
	): Location<any> => ({
		...location,
		pathname: parser(location.pathname),
	});

	const setNewHistoryLocation = (
		type: 'replace' | 'push',
		pathOrLocation: string | LocationDescriptorObject<any>,
		state?: any,
	) => {
		const setHistory = type === 'push' ? history.push : history.replace;

		if (typeof pathOrLocation === 'string') {
			return setHistory(extensionRouteToFullPath(pathOrLocation), state);
		}

		return setHistory(
			parseLocation({ state, ...pathOrLocation } as Location<any>, extensionRouteToFullPath),
		);
	};

	return {
		...history,
		location: parseLocation(history.location, fullPathToExtensionRoute),
		push(pathOrLocation: string | LocationDescriptorObject<any>, state?: any) {
			setNewHistoryLocation('push', pathOrLocation, state);
		},
		replace(pathOrLocation: string | LocationDescriptorObject<any>, state?: any) {
			setNewHistoryLocation('replace', pathOrLocation, state);
		},
		listen(listener) {
			if (fg('forge-ui-history-listen-cleanup')) {
				const unlistenHistory = history.listen((location, action) => {
					// Across the v2 bridge `listener` is a proxy that returns a Promise, which rejects
					// with ConnectionClosedError once the connection is gone.
					Promise.resolve(
						listener(parseLocation(location, fullPathToExtensionRoute), action),
					).catch((error: unknown) => {
						if (isConnectionClosedError(error)) {
							// The app can no longer be notified, so stop listening.
							unlisten();
						}
					});
				});

				const unlisten = () => {
					unlistenRegistry?.delete(unlisten);
					unlistenHistory();
				};
				unlistenRegistry?.add(unlisten);

				return unlisten;
			}

			const unlisten = history.listen((location, action) => {
				listener(parseLocation(location, fullPathToExtensionRoute), action);
			});

			return unlisten;
		},
	};
};
